//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { RIGHTS_STATUS } from 'app/bulk-actions/bulk-actions-constants/bulk-actions-constants';

type GalleryStatusText = {
  [key in RIGHTS_STATUS]?: {
    fanreel: string;
    default: string;
  };
};

const GALLERY_STATUS_TEXT: GalleryStatusText = {
  none: {
    fanreel: `This media belongs to a carousel post so rights will be requested for all media within the
              carousel. Upon poster\\'s approval, only this piece of media will be distributed to Fanreel.`,
    default: `This media belongs to a carousel post. Rights will be requested for all media within the carousel
              via a single request.`,
  },
  pending: {
    fanreel: `You've already requested rights for the media in this carousel post. Click 'Approve' below to
              distribute this to Fanreel upon poster's approval`,
    default: `You've already requested rights for the media in this carousel post. Upon the poster's approval,
              the status of this media will become 'Approved'.`,
  },
  initiated: {
    fanreel: `You've already requested rights for the media in this carousel post. Click 'Approve' below to
              distribute this to Fanreel upon poster's approval`,
    default: `You've already requested rights for the media in this carousel post. Upon the poster's approval,
              the status of this media will become 'Approved'.`,
  },
  approved: {
    fanreel: `Rights have been approved for the carousel post that this media belongs to. Click 'Approve'
              below to distribute to your Fanreel.`,
    default:
      'Rights have been approved for the carousel post that this media belongs to.',
  },
  expired: {
    fanreel:
      'Rights have been rejected for the carousel post this media belongs to.',
    default:
      'Rights have been rejected for the carousel post this media belongs to.',
  },
};

const TWITTER_GALLERY_STATUS_TEXT: GalleryStatusText = {
  none: {
    fanreel: `This media belongs to a post with multiple images, so rights will be requested for all media
              within the post. Upon poster's approval, only this piece of media will be distributed to
              Fanreel.`,
    default: `This media belongs to a post with multiple images. Rights will be requested for all media within
              the post via a single request.`,
  },
  pending: {
    fanreel: `You've already requested rights for the media in this post. Click 'Approve' below to distribute
              this to Fanreel upon poster's approval`,
    default: `You've already requested rights for the media in this post. Upon the poster's approval, the
              status of this media will become 'Approved'.`,
  },
  approved: {
    fanreel: `Rights have been approved for the post that this media belongs to. Click 'Approve' below to
              distribute to your Fanreel.`,
    default:
      'Rights have been approved for the post that this media belongs to.',
  },
  expired: {
    fanreel:
      'Rights have been rejected for the post that this media belongs to.',
    default:
      'Rights have been rejected for the post that this media belongs to.',
  },
};

export const getGalleryText = (
  status: RIGHTS_STATUS,
  currentlyInWorkflow: boolean,
) => {
  const statusText = GALLERY_STATUS_TEXT[status];
  if (!statusText) {
    return 'This media belongs to a Carousel post';
  }
  return currentlyInWorkflow ? statusText.fanreel : statusText.default;
};

export const getTwitterGalleryText = (
  status: RIGHTS_STATUS,
  currentlyInWorkflow: boolean,
) => {
  const statusText = TWITTER_GALLERY_STATUS_TEXT[status];
  if (!statusText) {
    return 'This post has multiple images attached, so rights will be requested for all images within the post.';
  }
  return currentlyInWorkflow ? statusText.fanreel : statusText.default;
};
